import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import type { DownlineQuery, DownlineResult } from '@/data/api'
import { unstopped, type Account, type AccountFlag, type Role } from '@/data/types'
import { agentNodeToAccount, levelToRole, type AgentNode } from '@/api/map/agent'
import { inrMajorToDisplay, paiseToDisplay, parseCurrencyType } from '@/lib/currency'
import { fromMinorUnitsSigned } from '@/api/map/pnl'
import { STOP_COPY } from '@/lib/status'

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** analytics-service Money — minor units as string. */
export type MoneyMinor = string

/** analytics-service `Projected` (ADR-0003). */
export interface AnalyticsProjected {
  valueMinor?: MoneyMinor
  stale: boolean
  projectedAt?: string | null
}

/** analytics-service `DownlineRow` (OpenAPI). */
export interface AnalyticsDownlineRow {
  id: string
  name: string
  role: string
  level: number
  isMember: boolean
  subtreeMembers?: number
  creditLimitMinor?: MoneyMinor
  balanceMinor: MoneyMinor
  creditLimitUsedPct?: number | null
  exposure: AnalyticsProjected
  turnoverMinor?: MoneyMinor
  bookNetMinor?: MoneyMinor
  lastBetDate?: string | null
  dormant30d?: boolean
  neverPlacedABet?: boolean
}

/** analytics-service `DownlinePage` (OpenAPI). */
export interface AnalyticsDownlinePage {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  page?: number
  size?: number
  total?: number
  rows: AnalyticsDownlineRow[]
}

function analyticsRoleToConsole(row: AnalyticsDownlineRow): Role {
  if (row.isMember || row.role === 'member') return 'player'
  return levelToRole(row.level)
}

function analyticsFlags(row: AnalyticsDownlineRow): AccountFlag[] {
  const flags: AccountFlag[] = []
  const used = row.creditLimitUsedPct
  if (typeof used === 'number') {
    if (used > 1) flags.push('exposure-over-limit')
    else if (used > 0.8) flags.push('exposure-near-limit')
  }
  if (row.dormant30d) flags.push('dormant-funded')
  return flags
}

/** Map one analytics DownlineRow → console Account (direct child of `parentId`). */
export function analyticsDownlineRowToAccount(
  row: AnalyticsDownlineRow,
  parentId: string,
): Account {
  const role = analyticsRoleToConsole(row)
  const ccy = parseCurrencyType((row as { currencyType?: string }).currencyType)
  const exposure =
    row.exposure.stale || row.exposure.valueMinor == null
      ? 0
      : Math.max(0, inrMajorToDisplay(fromMinorUnitsSigned(row.exposure.valueMinor), ccy))
  return {
    id: row.id,
    username: row.name,
    displayName: row.name,
    role,
    parentId,
    path: [parentId],
    status: 'active',
    createdAt: '',
    lastActiveAt: row.lastBetDate ? `${row.lastBetDate}T00:00:00.000Z` : null,
    currencyType: ccy,
    creditLimit: paiseToDisplay(row.creditLimitMinor, ccy),
    balance: inrMajorToDisplay(fromMinorUnitsSigned(row.balanceMinor), ccy),
    distributedBalance: 0,
    exposure,
    // Give/Take settlement is not on the analytics downline contract.
    settlement: 0,
    share: { sportsbook: 0, casino: 0 },
    commission: { fancy: 0, bookmaker: 0, matchOdds: 0 },
    betLimits: { min: 0, max: 0 },
    games: {
      casino: true,
      cardGame: true,
      liveGame: true,
      sportsbook: true,
    },
    childCount: 0,
    memberCount: row.subtreeMembers ?? 0,
    flags: analyticsFlags(row),
    ...unstopped('active', STOP_COPY.notWiredLive),
  }
}

/**
 * Map analytics `GET /agents/{id}/downline` → console DownlineResult.
 * Server already pages/searches; remaining filters/sorts apply within the page
 * (OpenAPI: no live-value sort on the server).
 */
export function toAnalyticsDownlineResult(
  page: AnalyticsDownlinePage,
  rootId: string,
  query: DownlineQuery = {},
): DownlineResult {
  const {
    roles,
    statuses,
    hasExposure,
    needsAttention,
    sort = 'username',
    direction = 'asc',
    page: consolePage = 1,
    pageSize = DEFAULT_PAGE_SIZE,
  } = query

  let rows = (page.rows ?? []).map((row) => analyticsDownlineRowToAccount(row, rootId))

  rows = rows
    .filter((a) => !roles?.length || roles.includes(a.role))
    .filter((a) => !statuses?.length || statuses.includes(a.status))
    .filter((a) => !hasExposure || a.exposure > 0)
    .filter((a) => !needsAttention || a.flags.length > 0)

  const factor = direction === 'asc' ? 1 : -1
  if (sort === 'balance' || sort === 'exposure' || sort === 'creditLimit' || sort === 'settlement') {
    rows = [...rows].sort((a, b) => ((a[sort] as number) - (b[sort] as number)) * factor)
  } else if (sort === 'lastActiveAt') {
    rows = [...rows].sort((a, b) => {
      const av = a.lastActiveAt ? Date.parse(a.lastActiveAt) : -1
      const bv = b.lastActiveAt ? Date.parse(b.lastActiveAt) : -1
      return (av - bv) * factor
    })
  } else if (sort === 'username') {
    rows = [...rows].sort((a, b) => a.username.localeCompare(b.username, 'en-IN') * factor)
  }

  const totals = rows.reduce(
    (acc, row) => {
      acc.creditLimit += row.creditLimit
      acc.balance += row.balance
      acc.exposure += row.exposure
      if (row.settlement > 0) acc.toCollect += row.settlement
      else acc.toPay += -row.settlement
      if (row.role === 'player') acc.members += 1
      else acc.agents += 1
      return acc
    },
    { creditLimit: 0, balance: 0, exposure: 0, toCollect: 0, toPay: 0, members: 0, agents: 0 },
  )

  const total = typeof page.total === 'number' ? page.total : rows.length

  return {
    rows,
    total,
    page: consolePage,
    pageSize,
    totals: {
      creditLimit: round2(totals.creditLimit),
      balance: round2(totals.balance),
      exposure: round2(totals.exposure),
      toCollect: round2(totals.toCollect),
      toPay: round2(totals.toPay),
      members: totals.members,
      agents: totals.agents,
    },
  }
}

/** Attach childCount / memberCount from adjacency in the flat tree. */
export function enrichTreeAccounts(nodes: AgentNode[]): Account[] {
  const byNode = new Map(nodes.map((n) => [n.id, n]))
  const accounts = nodes.map((n) =>
    agentNodeToAccount(n, n.parentId ? byNode.get(n.parentId) : null),
  )
  const byId = new Map(accounts.map((a) => [a.id, a]))
  const byParent = new Map<string | null, Account[]>()
  for (const a of accounts) {
    const key = a.parentId
    const list = byParent.get(key) ?? []
    list.push(a)
    byParent.set(key, list)
  }
  for (const a of accounts) {
    const path: string[] = []
    let cursor = a.parentId
    const seen = new Set<string>()
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor)
      path.unshift(cursor)
      cursor = byId.get(cursor)?.parentId ?? null
    }
    a.path = path
    const kids = byParent.get(a.id) ?? []
    a.childCount = kids.length
    // Prefer hierarchy get() distributedBalance; else Σ children creditLimits from tree.
    if (!a.distributedBalance) {
      a.distributedBalance = kids.reduce((sum, k) => sum + k.creditLimit, 0)
    }
    a.memberCount =
      a.role === 'player'
        ? 0
        : kids.reduce((sum, k) => sum + (k.role === 'player' ? 1 : k.memberCount), 0)
  }
  // Second pass: members under agents that themselves have agents need rollup.
  // Tree is shallow enough for a few passes.
  for (let pass = 0; pass < 6; pass++) {
    for (const a of accounts) {
      if (a.role === 'player') continue
      const kids = byParent.get(a.id) ?? []
      a.memberCount = kids.reduce(
        (sum, k) => sum + (k.role === 'player' ? 1 : k.memberCount),
        0,
      )
    }
  }
  return accounts
}

/** Client-side filter / sort / page over hierarchy tree accounts. */
export function toDownlineResult(
  nodes: AgentNode[],
  rootId: string,
  query: DownlineQuery = {},
): DownlineResult {
  const {
    search = '',
    roles,
    statuses,
    hasExposure,
    needsAttention,
    settlement,
    directOnly = false,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    sort = 'username',
    direction = 'asc',
  } = query

  const accounts = enrichTreeAccounts(nodes)
  const byId = new Map(accounts.map((a) => [a.id, a]))

  let rows = accounts.filter((a) => a.id !== rootId)
  if (directOnly) {
    rows = rows.filter((a) => a.parentId === rootId)
  } else {
    // Whole subtree under root (already scoped by API); drop anyone outside path.
    rows = rows.filter((a) => {
      if (a.parentId === rootId) return true
      let cur: Account | undefined = a
      const seen = new Set<string>()
      while (cur?.parentId) {
        if (cur.parentId === rootId) return true
        if (seen.has(cur.parentId)) break
        seen.add(cur.parentId)
        cur = byId.get(cur.parentId)
      }
      return false
    })
  }

  const needle = search.trim().toLowerCase()
  rows = rows
    .filter(
      (a) =>
        !needle ||
        a.username.toLowerCase().includes(needle) ||
        a.displayName.toLowerCase().includes(needle),
    )
    .filter((a) => !roles?.length || roles.includes(a.role))
    .filter((a) => !statuses?.length || statuses.includes(a.status))
    .filter((a) => !hasExposure || a.exposure > 0)
    .filter((a) => !needsAttention || a.flags.length > 0)
    // Mirrors `listDownline` exactly. If the fixture path and the live path
    // filtered differently, the same URL would show different rows depending on
    // VITE_API_BASE and a bulk plan built from one would not match the table
    // built from the other.
    .filter((a) =>
      !settlement ? true : settlement === 'collect' ? a.settlement > 0 : a.settlement < 0,
    )

  const factor = direction === 'asc' ? 1 : -1
  rows = [...rows].sort((a, b) => {
    switch (sort) {
      case 'username':
        return a.username.localeCompare(b.username, 'en-IN') * factor
      case 'lastActiveAt': {
        const av = a.lastActiveAt ? Date.parse(a.lastActiveAt) : -1
        const bv = b.lastActiveAt ? Date.parse(b.lastActiveAt) : -1
        return (av - bv) * factor
      }
      default:
        return ((a[sort] as number) - (b[sort] as number)) * factor
    }
  })

  const totals = rows.reduce(
    (acc, row) => {
      acc.creditLimit += row.creditLimit
      acc.balance += row.balance
      acc.exposure += row.exposure
      if (row.settlement > 0) acc.toCollect += row.settlement
      else acc.toPay += -row.settlement
      if (row.role === 'player') acc.members += 1
      else acc.agents += 1
      return acc
    },
    { creditLimit: 0, balance: 0, exposure: 0, toCollect: 0, toPay: 0, members: 0, agents: 0 },
  )

  const start = (page - 1) * pageSize
  return {
    rows: rows.slice(start, start + pageSize),
    total: rows.length,
    page,
    pageSize,
    totals: {
      creditLimit: round2(totals.creditLimit),
      balance: round2(totals.balance),
      exposure: round2(totals.exposure),
      toCollect: round2(totals.toCollect),
      toPay: round2(totals.toPay),
      members: totals.members,
      agents: totals.agents,
    },
  }
}
